import React, { useCallback } from "react";
import { AccessEnum } from "~/api/GeneratedApi";
import { apiAccessesToState } from "~/components/AccessWrapper";
import {
  selectAccesses,
  selectAccessesSet,
  selectAnyAccess,
  selectIsGlobalAdmin,
  selectKeys,
  selectTransformedAccesses,
} from "~/redux/reducers/authReducer";
import { useAppSelector } from "~/redux/store/store";

type UserAccess = Record<AccessKey, boolean>;

type AccessKey = keyof typeof AccessEnum;

type AccessKeyRecord = Record<AccessEnum, keyof typeof AccessEnum>;

export type Accesses = Record<AccessEnum, boolean>;

type GetAccessReturn = UserAccess;

type GetAccessArg = number | number[] | undefined;

type UseAccessReturn =
  // List of resources the user has access to through any map area
  UserAccess & {
    // Check user access to a specific map area or map areas
    getAccess: (mapAreaId: GetAccessArg) => GetAccessReturn;
    isGlobalAdmin: boolean;
    accesses: Accesses;
    keys: AccessKeyRecord;
    accessesSet: boolean;
    hasAccess: (
      access: AccessEnum | AccessEnum[],
      mapAreaId?: number,
    ) => boolean;
  };

const generateEmptyAccessTable = (): UserAccess => {
  return Object.keys(AccessEnum).reduce(
    (total, curr) => ({ ...total, [curr]: false }),
    {},
  ) as UserAccess;
};

export function useAccess(): UseAccessReturn {
  const accesses = useAppSelector(selectAccesses);
  const accessesSet = useAppSelector(selectAccessesSet);
  const transformedAccesses = useAppSelector(selectTransformedAccesses);
  const anyAccess = useAppSelector(selectAnyAccess);
  const keys = useAppSelector(selectKeys);
  const isGlobalAdmin = useAppSelector(selectIsGlobalAdmin);

  const getAccess = useCallback(
    (mapAreaId: GetAccessArg): GetAccessReturn => {
      if (!accesses || !anyAccess) return generateEmptyAccessTable();
      if (!mapAreaId) return anyAccess;
      return apiAccessesToState(accesses, mapAreaId);
    },
    [anyAccess, accesses],
  );

  const hasAccess = useCallback(
    (access: AccessEnum | AccessEnum[], mapAreaId?: number) => {
      const requiredAccesses = Array.isArray(access) ? access : [access];
      if (mapAreaId) {
        const mapAreaAccess = getAccess(mapAreaId);
        return requiredAccesses.some(
          (x) => mapAreaAccess[x as unknown as AccessKey],
        );
      }

      return requiredAccesses.some((x) => transformedAccesses![x]);
    },
    [transformedAccesses, getAccess],
  );

  const anyAccessMemo = React.useMemo(
    () => anyAccess ?? generateEmptyAccessTable(),
    [anyAccess],
  );
  return {
    getAccess,
    ...anyAccessMemo,
    isGlobalAdmin,
    accesses: transformedAccesses as Accesses,
    keys: keys as AccessKeyRecord,
    accessesSet,
    hasAccess,
  };
}
